import type { ExtractedFact } from 'services/imports/buildAmendmentPrompt';
import type { ImportPage } from 'services/imports/fileToPages';
import { reviewAmendments } from 'services/imports/reviewAmendments';
import type { AmendmentSuggestion } from 'services/imports/reviewAmendments';
import { toPageLines } from 'views/imports/run-extraction';
import type { OcrPageState, PageIssue, RunProgress } from 'views/imports/types';
import { toDisplayMessage } from 'views/imports/utils';

const CANCELLED_MESSAGE = 'cancelled';

export type ReviewImage = (prompt: string, imageDataUrl: string) => Promise<string>;

export type RunReviewInput = {
  pages: readonly ImportPage[];
  ocrByPage: ReadonlyMap<string, OcrPageState>;
  extractedByPage: ReadonlyMap<string, readonly ExtractedFact[]>;
  reviewImage: ReviewImage;
  signal: AbortSignal;
  onProgress: (progress: RunProgress) => void;
};

export type RunReviewResult = {
  amendments: AmendmentSuggestion[];
  reviewedPageIds: string[];
  failedPageIds: string[];
  issues: PageIssue[];
  cancelled: boolean;
};

export const runReview = async (input: RunReviewInput): Promise<RunReviewResult> => {
  const { pages, ocrByPage, extractedByPage, reviewImage, signal, onProgress } = input;
  const queue = pages.filter((page) => !page.failed && ocrByPage.get(page.id)?.status === 'done');
  const lines = toPageLines(ocrByPage);

  let calls = 0;
  const guardedReviewImage: ReviewImage = async (prompt, imageDataUrl) => {
    if (signal.aborted) throw new Error(CANCELLED_MESSAGE);
    onProgress({ current: calls, total: queue.length });
    calls += 1;
    try {
      return await reviewImage(prompt, imageDataUrl);
    } catch (error) {
      if (signal.aborted) throw new Error(CANCELLED_MESSAGE);
      throw error;
    }
  };

  const { amendments, errors } = await reviewAmendments({
    pages: queue,
    lines,
    extractedByPage,
    reviewImage: guardedReviewImage,
  });
  const realErrors = errors.filter((error) => error.message !== CANCELLED_MESSAGE);
  const erroredIds = new Set(errors.map((error) => error.pageId));

  return {
    amendments,
    reviewedPageIds: queue.filter((page) => !erroredIds.has(page.id)).map((page) => page.id),
    // Cancelled pages are kept as failed so Retry review can pick them up again.
    failedPageIds: errors.map((error) => error.pageId),
    issues: realErrors.map((error) => ({ pageId: error.pageId, kind: 'review-error', detail: toDisplayMessage(error.message) })),
    cancelled: signal.aborted,
  };
};
